/**
 * O endereço da barra decide qual tela aparece.
 *
 * São duas telas e meia, então não entra biblioteca de rota: trinta linhas aqui custam menos
 * do que uma dependência a mais numa página que o pastor abre no celular com internet ruim.
 *
 * **Por que rota de verdade, e não um estado na memória**: o Relatório de Movimentação é a
 * própria tela, fotografada por um Chromium dentro do servidor. Para fotografar, ele precisa
 * ABRIR um endereço. Tela que só existe depois de alguém clicar não tem endereço, e não teria
 * como ser fotografada sem desenhar uma segunda versão dela, que é exatamente o que a decisão
 * 3 da parte 3 não quer.
 */

export type Rota =
  /** O dia que o cartão "Hoje" está mostrando. Nulo é hoje. */
  | { tela: 'inicio'; dia: string | null }
  | { tela: 'congregacao'; id: string; competencia: string | null }
  /** A folha de saídas de um mês de uma congregação, da fase 4 do espelho. */
  | { tela: 'saidas-da-congregacao'; id: string; competencia: string | null }
  /** O fechamento do mês de uma congregação, da fase 5 do espelho. */
  | { tela: 'fechamento-da-congregacao'; id: string; competencia: string | null }
  /* As telas da fase 6 do espelho (19/10/2026). */
  | { tela: 'dizimistas'; id: string; competencia: string | null }
  | { tela: 'repasses-da-congregacao'; id: string; competencia: string | null }
  | { tela: 'agenda'; id: string; competencia: string | null }
  | { tela: 'repasses-do-campo'; competencia: string | null }
  | { tela: 'conferencia-do-cartao'; competencia: string | null }
  | { tela: 'campo'; competencia: string | null; versao: number | null }
  | { tela: 'aguardando' }
  | { tela: 'saidas' }
  | { tela: 'notebooks' }
  | { tela: 'pessoas' }
  | { tela: 'auditoria' }
  | { tela: 'segundo-fator' }
  /** Trocar a própria senha, já dentro do painel. Diferente do link do e-mail, que é sem sessão. */
  | { tela: 'minha-senha' }
  /**
   * A página do link de trocar a senha. **A única que funciona sem sessão.**
   *
   * O token vem no caminho, e não em `?token=`: endereço com pergunta é o que vaza em `Referer`
   * e o que aparece em registro de servidor pelo caminho.
   */
  | { tela: 'trocar-senha'; token: string }
  /**
   * A tela de Sobre. **A outra que funciona sem sessão**, e pelo mesmo motivo: quem pergunta se
   * o servidor está no ar costuma estar do lado de fora.
   */
  | { tela: 'sobre' }
  /**
   * A TELA DE ADMINISTRAÇÃO, de quem hospeda o sistema.
   *
   * Ela é a terceira que não depende da sessão do painel, e por um motivo diferente das outras
   * duas: ela tem sessão PRÓPRIA, noutro cookie, noutra porta. Perguntar "quem sou eu" ao painel
   * antes de abrir esta tela mostraria a tela de entrar do pastor a quem quer a Administração.
   */
  | { tela: 'administracao'; parte: 'campos' | 'caixa-de-saida' }

/** `?impressao=1`: a tela sai sem barra, sem botão e sem navegação, só o papel. */
export function ehParaImprimir(busca: string = window.location.search): boolean {
  return new URLSearchParams(busca).get('impressao') === '1'
}

export function rotaDe(caminho: string, busca: string): Rota {
  /*
    As saídas vêm ANTES da congregação: o endereço delas começa igual, e a outra expressão
    casaria com ele se viesse primeiro.
  */
  /* As telas da fase 6: as de congregação também vêm antes da congregação. */
  const daFase6 = /^\/congregacoes\/([^/]+)\/(dizimistas|repasses|agenda)\/?$/.exec(caminho)
  if (daFase6) {
    const qual = daFase6[2] === 'repasses' ? 'repasses-da-congregacao' : (daFase6[2] as 'dizimistas' | 'agenda')
    return {
      tela: qual,
      id: decodeURIComponent(daFase6[1]!),
      competencia: new URLSearchParams(busca).get('competencia')
    }
  }
  if (/^\/campo\/repasses\/?$/.test(caminho)) {
    return { tela: 'repasses-do-campo', competencia: new URLSearchParams(busca).get('competencia') }
  }
  if (/^\/campo\/conferencia-do-cartao\/?$/.test(caminho)) {
    return { tela: 'conferencia-do-cartao', competencia: new URLSearchParams(busca).get('competencia') }
  }

  const fechamentoDaCong = /^\/congregacoes\/([^/]+)\/fechamento\/?$/.exec(caminho)
  if (fechamentoDaCong) {
    return {
      tela: 'fechamento-da-congregacao',
      id: decodeURIComponent(fechamentoDaCong[1]!),
      competencia: new URLSearchParams(busca).get('competencia')
    }
  }

  const saidasDaCong = /^\/congregacoes\/([^/]+)\/saidas\/?$/.exec(caminho)
  if (saidasDaCong) {
    return {
      tela: 'saidas-da-congregacao',
      id: decodeURIComponent(saidasDaCong[1]!),
      competencia: new URLSearchParams(busca).get('competencia')
    }
  }

  const congregacao = /^\/congregacoes\/([^/]+)\/?$/.exec(caminho)
  if (congregacao) {
    return {
      tela: 'congregacao',
      id: decodeURIComponent(congregacao[1]!),
      competencia: new URLSearchParams(busca).get('competencia')
    }
  }

  if (/^\/campo\/?$/.test(caminho)) {
    const query = new URLSearchParams(busca)
    const versao = Number(query.get('versao'))
    return {
      tela: 'campo',
      competencia: query.get('competencia'),
      // A versão só vale quando é um número de verdade: `?versao=abc` é a última assinada.
      versao: Number.isInteger(versao) && versao > 0 ? versao : null
    }
  }

  const trocarSenha = /^\/trocar-senha\/([^/]+)\/?$/.exec(caminho)
  if (trocarSenha) {
    return { tela: 'trocar-senha', token: decodeURIComponent(trocarSenha[1]!) }
  }

  if (/^\/sobre\/?$/.test(caminho)) return { tela: 'sobre' }
  if (/^\/administracao\/?$/.test(caminho)) return { tela: 'administracao', parte: 'campos' }
  if (/^\/administracao\/caixa-de-saida\/?$/.test(caminho)) {
    return { tela: 'administracao', parte: 'caixa-de-saida' }
  }
  if (/^\/aguardando\/?$/.test(caminho)) return { tela: 'aguardando' }
  if (/^\/saidas\/?$/.test(caminho)) return { tela: 'saidas' }
  if (/^\/notebooks\/?$/.test(caminho)) return { tela: 'notebooks' }
  if (/^\/pessoas\/?$/.test(caminho)) return { tela: 'pessoas' }
  if (/^\/auditoria\/?$/.test(caminho)) return { tela: 'auditoria' }
  if (/^\/segundo-fator\/?$/.test(caminho)) return { tela: 'segundo-fator' }
  if (/^\/minha-senha\/?$/.test(caminho)) return { tela: 'minha-senha' }

  return { tela: 'inicio', dia: new URLSearchParams(busca).get('dia') }
}

/**
 * O endereço do Início, com o dia do cartão "Hoje".
 *
 * **No endereço, e não num estado na memória**, pelo mesmo motivo do cabeçalho deste arquivo:
 * recarregar a página no celular não pode devolver o pastor para hoje depois de ele ter andado
 * seis dias para trás, e o link que ele manda à tesoureira precisa abrir no mesmo dia.
 */
export function enderecoDoInicio(dia?: string | null): string {
  return dia ? `/?dia=${dia}` : '/'
}

export function rotaDeAgora(): Rota {
  return rotaDe(window.location.pathname, window.location.search)
}

/** O endereço de uma congregação, com o mês quando ele não é o de hoje. */
/** O endereço do fechamento de uma congregação. */
export function enderecoDoFechamentoDaCongregacao(
  id: string, competencia?: string | null
): string {
  const caminho = `/congregacoes/${encodeURIComponent(id)}/fechamento`
  return competencia ? `${caminho}?competencia=${competencia}` : caminho
}

/** O endereço da folha de saídas de uma congregação. */
export function enderecoDasSaidasDaCongregacao(id: string, competencia?: string | null): string {
  const caminho = `/congregacoes/${encodeURIComponent(id)}/saidas`
  return competencia ? `${caminho}?competencia=${competencia}` : caminho
}

export function enderecoDaCongregacao(
  id: string,
  competencia?: string | null,
  /**
   * Abrir a folha já rolada até a linha deste dia. É o atalho do cartão "Hoje" do Início.
   *
   * Era um `true` que queria dizer "hoje". Desde 13/10/2026 o cartão anda para qualquer dia, e
   * o atalho precisa levar QUAL dia: senão, olhando o domingo passado, o botão abriria a folha
   * marcando hoje.
   */
  noDia?: string | null
): string {
  const caminho = `/congregacoes/${encodeURIComponent(id)}`
  const comMes = competencia ? `${caminho}?competencia=${competencia}` : caminho
  return noDia ? `${comMes}#dia-${noDia}` : comMes
}

/** O endereço da tela do Campo. A versão só entra quando se quer uma assinatura antiga. */
export function enderecoDoCampo(competencia?: string | null, versao?: number | null): string {
  const partes = [
    ...(competencia ? [`competencia=${competencia}`] : []),
    ...(versao ? [`versao=${versao}`] : [])
  ]
  return partes.length === 0 ? '/campo' : `/campo?${partes.join('&')}`
}

/**
 * Anda para outro endereço sem recarregar a página.
 *
 * `pushState` e não `location.href`: recarregar a página inteira a cada clique numa conexão
 * ruim é meio segundo de tela branca que não precisa existir.
 */
export function irPara(endereco: string): void {
  window.history.pushState(null, '', endereco)
  window.dispatchEvent(new PopStateEvent('popstate'))
}

/** O endereço de uma tela de congregação da fase 6 (dizimistas, repasses, agenda). */
export function enderecoDaTelaDaCongregacao(
  qual: 'dizimistas' | 'repasses' | 'agenda', id: string, competencia?: string | null
): string {
  const caminho = `/congregacoes/${encodeURIComponent(id)}/${qual}`
  return competencia ? `${caminho}?competencia=${competencia}` : caminho
}

/** O endereço de uma tela do Campo da fase 6 (repasses, conferência da maquininha). */
export function enderecoDaTelaDoCampo(
  qual: 'repasses' | 'conferencia-do-cartao', competencia?: string | null
): string {
  const caminho = `/campo/${qual}`
  return competencia ? `${caminho}?competencia=${competencia}` : caminho
}
